import { useEffect, useState } from 'react'
import { financeReportsApi, type CustomerValue } from '../api/financeReports'
import { eur, errMsg } from '../lib/financeFormat'
import { useFeatureFlag } from '../lib/featureFlags'
import { Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, KeyValueList } from './ui'

function monthIt(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
}

/**
 * C-07 — scheda cliente: valore del cliente mese per mese (ricavi maturati + ricorrenti della mandante − provvigioni − costo di
 * acquisizione). Completa il riquadro "Quanto hanno fruttato le pratiche" (movimenti mandante) senza ripeterlo: qui si parte dal
 * conto economico. Compare solo con il flag `finanza.margini_livelli`; un 403/404 la omette in silenzio. Importi solo con
 * `CanSeeRealAmounts`.
 */
export function CustomerValuePanel({ contactId }: { contactId: number }) {
  const on = useFeatureFlag('finanza.margini_livelli')
  const [data, setData] = useState<CustomerValue | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!on) return
    let alive = true
    setData(null)
    setError('')
    financeReportsApi.customerValue(contactId)
      .then(r => { if (alive) setData(r) })
      .catch(e => { if (alive && !/403|404|forbidden|not found/i.test(errMsg(e, ''))) setError(errMsg(e, 'Valore del cliente non disponibile.')) })
    return () => { alive = false }
  }, [contactId, on])

  if (!on) return null
  if (error) return <Callout tone="error">{error}</Callout>
  if (!data) return null
  const t = data.totals

  return (
    <Card variant="outlined" padding="none">
      <CardHeader icon="trending_up" title="Valore del cliente" />
      {data.canSeeAmounts && (
        <DataTable variant="plain" aria-label="Valore del cliente mese per mese">
          <thead>
            <tr>
              <th>Mese</th><th className="text-end">Ricavi</th><th className="text-end">Ricorrenti</th>
              <th className="text-end">Provvigioni</th><th className="text-end">Acquisizione</th><th className="text-end">Valore</th>
            </tr>
          </thead>
          <tbody>
            {(data.months ?? []).length === 0 && <DataTableEmptyRow colSpan={6} title="Nessun ricavo" description="Nessun ricavo maturato per questo cliente." />}
            {(data.months ?? []).map(m => (
              <tr key={m.month}>
                <td>{monthIt(m.month)}</td>
                <td className="text-end md-num">{eur(m.revenue)}</td>
                <td className="text-end md-num">{eur(m.recurring)}</td>
                <td className="text-end md-num">{eur(m.commissions)}</td>
                <td className="text-end md-num">{eur(m.acquisitionCost)}</td>
                <td className="text-end md-num">{eur(m.value)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      <CardContent>
        {t ? (
          <KeyValueList
            className="md-typescale-body-small"
            items={[
              { label: 'Ricavi maturati', value: <>{eur(t.revenue)}</> },
              { label: 'Ricorrenti (di Equal)', value: <>{eur(t.recurring)}</> },
              { label: 'Provvigioni', value: <>{eur(t.commissions)}</> },
              { label: 'Costo di acquisizione', value: <>{eur(t.acquisitionCost)}</> },
              { label: 'Valore del cliente', value: <strong>{eur(t.value)}</strong> },
            ]}
          />
        ) : (
          <div className="text-muted md-typescale-body-small">Gli importi non sono visibili con il tuo profilo.</div>
        )}
        <div className="text-muted md-typescale-body-small mt-2">{data.definition}</div>
      </CardContent>
    </Card>
  )
}
